import React from 'react';
import {
    SagaEditor,
    isTableCell,
    EditorOperations,
    assertNonNull,
    TABLE_CONSTANTS,
    isTable,
    Colors,
} from '@saga/shared';
import { ReactEditor, useSlate, useSelected } from 'slate-react';
import { Node, NodeEntry, Editor, Transforms } from 'slate';
import classNames from 'classnames';
import * as Popover from '@radix-ui/react-popover';
import { MoreHorizontal } from 'react-feather';
import Button from '@/components/styled/Button';
import { useTranslation } from 'react-i18next';
import { TableResizeHandle } from './TableResizeHandle';
import { TableColumnMenu } from './TableColumnMenu';

// Import contexts from TableContexts.tsx
import { TableDimensionsContext, TableSelectionContext, TableHoverContext } from './TableContexts';

// Helper hooks for accessing split contexts
const useTableDimensions = () => {
    const context = React.useContext(TableDimensionsContext);
    assertNonNull(context, 'useTableDimensions must be used inside a TableContextProvider');
    return context;
};

const useTableSelection = () => {
    const context = React.useContext(TableSelectionContext);
    assertNonNull(context, 'useTableSelection must be used inside a TableContextProvider');
    return context;
};

const useTableHover = () => {
    const context = React.useContext(TableHoverContext);
    assertNonNull(context, 'useTableHover must be used inside a TableContextProvider');
    return context;
};

function getNodeEntry<T extends Node>(editor: ReactEditor, element: T): NodeEntry<T> {
    const path = ReactEditor.findPath(editor, element);
    return [element, path];
}

const isValidTailwindColorClass = (color: string): boolean => {
    return color.startsWith('bg-saga-') || color === 'blank';
};

const TABLE_CELL_OPACITY = {
    LIGHT: 'bg-opacity-50',
    DARK: 'dark:bg-opacity-30',
} as const;

export const tableCellPlugin = SagaEditor.Plugins.createBlockPlugin({
    match: isTableCell,

    Component({ element, children }) {
        const { features, columnWidths, totalColumns, onColumnResize, onPreviewChange } = useTableDimensions();
        const { menuOpenForColumn, setMenuOpenForColumn } = useTableSelection();
        const { setHoveringResizeColumn } = useTableHover();
        const editor = useSlate();
        const selected = useSelected();
        const nodeEntry = getNodeEntry(editor, element);
        const column = EditorOperations.Tables.getColumnIndex(nodeEntry);
        const isWithinFirstRow = EditorOperations.Tables.isWithinFirstRow(nodeEntry);
        const columnSelected = EditorOperations.Tables.isColumnSelected(editor, nodeEntry, column);
        const menuOpen = menuOpenForColumn === column && isWithinFirstRow;
        const { t } = useTranslation();

        // Guard against missing columnWidths in read-only mode or old tables
        const columnWidth = columnWidths?.[column] || TABLE_CONSTANTS.DEFAULT_COLUMN_WIDTH;

        // Convert legacy color formats (e.g., 'yellow', 'blue') to current Tailwind classes
        // This ensures backwards compatibility with existing table data
        const rawBackgroundColor = element.backgroundColor;
        const convertedColor = rawBackgroundColor && Colors.convertOutdatedColor(rawBackgroundColor);
        // Validate the color class to protect against corrupted data
        const backgroundColor =
            convertedColor && isValidTailwindColorClass(convertedColor) ? convertedColor : undefined;
        // Check if cell has no background color ('blank' is a special value meaning "no color")
        const isBlankColor = !backgroundColor || backgroundColor === 'blank';

        return (
            <td
                id={element.id}
                style={{
                    width: columnWidth,
                    position: 'relative',
                    overflow: 'visible',
                }}
                className={classNames('break-word px-1 py-0.5', {
                    'bg-saga-selection-blue/10 dark:bg-saga-selection-blue/10':
                        menuOpenForColumn === column && isBlankColor,
                    'outline outline-2 outline-saga-selection-blue -outline-offset-1 rounded-sm': selected,
                    [`${backgroundColor} ${TABLE_CELL_OPACITY.LIGHT} ${TABLE_CELL_OPACITY.DARK}`]: !isBlankColor,
                })}
            >
                {children}
                {isWithinFirstRow && (columnSelected || menuOpen) && features && (
                    <Popover.Root
                        onOpenChange={(open) => {
                            if (open) {
                                setMenuOpenForColumn(column);
                            } else {
                                setMenuOpenForColumn(null);
                            }
                        }}
                        open={menuOpen}
                    >
                        <Popover.Trigger asChild>
                            <span
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                className="opacity-70 hover:opacity-100 transition-opacity z-[100] cursor-pointer absolute -top-2 h-4  left-0 right-0 flex justify-center items-center"
                            >
                                <div className="flex items-center justify-center bg-white dark:bg-zinc-600 rounded">
                                    <Button.Plain>
                                        <span className="h-full w-full flex justify-center rounded border dark:border-zinc-600 border-saga-gray-200">
                                            <MoreHorizontal size={16} />
                                            <span className="sr-only">
                                                {t('common.open_context_menu_for_column', { column: column + 1 })}
                                            </span>
                                        </span>
                                    </Button.Plain>
                                </div>
                            </span>
                        </Popover.Trigger>
                        <TableColumnMenu
                            column={column}
                            totalColumns={totalColumns}
                            onSortAscending={() => {
                                features.sortByColumn(column, 'asc');
                                setMenuOpenForColumn(null);
                            }}
                            onSortDescending={() => {
                                features.sortByColumn(column, 'desc');
                                setMenuOpenForColumn(null);
                            }}
                            onMoveLeft={() => {
                                features.moveColumnLeft(column);
                                setMenuOpenForColumn(null);
                            }}
                            onMoveRight={() => {
                                features.moveColumnRight(column);
                                setMenuOpenForColumn(null);
                            }}
                            onInsertLeft={() => {
                                EditorOperations.Tables.insertColumnBeforeTableCell(editor, nodeEntry);
                                setMenuOpenForColumn(null);
                            }}
                            onInsertRight={() => {
                                EditorOperations.Tables.insertColumnAfterTableCell(editor, nodeEntry);
                                setMenuOpenForColumn(null);
                            }}
                            onDelete={() => {
                                features.deleteColumnByIndex(column);
                                setMenuOpenForColumn(null);
                            }}
                            onSetBackgroundColor={(color: string) => {
                                // Apply color to all cells in the column
                                // Find the table element by traversing up the tree
                                const tableEntry = Editor.above(editor, {
                                    at: ReactEditor.findPath(editor, element),
                                    match: isTable,
                                });

                                if (!tableEntry) return;

                                const [tableNode, tablePath] = tableEntry;

                                Editor.withoutNormalizing(editor, () => {
                                    tableNode.children.forEach((row: any, rowIndex: number) => {
                                        const cellPath = [...tablePath, rowIndex, column];
                                        Transforms.setNodes(
                                            editor,
                                            { backgroundColor: color === 'blank' ? undefined : color },
                                            { at: cellPath },
                                        );
                                    });
                                });

                                setMenuOpenForColumn(null);
                            }}
                            onDuplicate={() => {
                                features.duplicateColumn(column);
                                setMenuOpenForColumn(null);
                            }}
                        />
                    </Popover.Root>
                )}
                {/* Resize handle - show for all columns when editable */}
                {features && (
                    <TableResizeHandle
                        columnIndex={column}
                        onResize={onColumnResize}
                        isActive={!!(selected || columnSelected)}
                        initialWidth={columnWidth}
                        setHoveringColumn={setHoveringResizeColumn}
                        onPreviewChange={onPreviewChange}
                    />
                )}
            </td>
        );
    },
});
